/**
 * S17 — mounts ViewTableView for an active view carrying a `table` clause. The
 * projection comes from the SAME machinery the board uses (`useViewSurface` →
 * `computeViewSurfaceResult`): governance closure, live knob-picker values,
 * view-INSTANCE parent resolution (an instance of a table-carrying parent
 * renders the inherited table), the plane pick, and the crash guard — a
 * throwing projection degrades to nothing-rendered + a HUD diagnostic instead
 * of unmounting the tree. Returns null when no table-view is active (the board
 * renders instead — see the representation switch in ViewerComposition).
 *
 * Cell edits (getter columns) route through the model-channel write path (S13a
 * `setFieldCellEdit` / setAspect) via the WYSIWYG mutation service: `cellWrite`
 * (tableWrites.ts) maps (row, column, value) → one typed `mutateModule` op;
 * read-only columns (calc / builtin / capability — annotated by
 * `computeTableData`) never write. The host opts in with `editable` (the same
 * gate as the board); without it — or without a workspace store — the table
 * renders display-only.
 */
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { computeTableData } from "@archlang/engine";
import { useModelMutationsOptional } from "../workbench/mutations.js";
import { useViewSurface } from "./useViewSurface.js";
import { cellWrite } from "./tableWrites.js";
import { useTableColumnEditing } from "./useTableColumnEditing.js";
import { ViewTableView } from "./ViewTableView.js";
import { useRegisterViewExport } from "./ViewExportContext.js";
import type { ViewExportAction } from "./export/ViewExportMenu.js";
import { tableToCsv, downloadText } from "./export/viewExport.js";
import { useTranslation } from "../i18n";

export function ViewTableContainer({ editable = false }: { editable?: boolean }): ReactNode {
  const { t } = useTranslation();
  const { t: ts } = useTranslation("sidebar");
  const { view, enginePlan, ctx } = useViewSurface();
  // Inline-edit feedback — same posture as the Sidebar's editNote: a refused
  // (stub row) or stale (compile catching up) cell commit must surface SOME
  // signal, never silently drop the typed value (r41). Cleared on the next
  // successful commit.
  const [editNote, setEditNote] = useState<string | null>(null);
  const mutations = useModelMutationsOptional();
  const columnOps = useTableColumnEditing(ctx);
  const data = useMemo(() => {
    if (!view || !ctx) return null;
    try {
      return computeTableData(view, enginePlan, ctx);
    } catch (e) {
      // Same crash posture as the board's plan computation: a throwing cell/
      // expression evaluation renders no table (the HUD carries the view's
      // diagnostics) — never an unmounted tree.
      console.debug("[view-table] table data evaluation failed:", e);
      return null;
    }
  }, [view, enginePlan, ctx]);

  const onEditCell = useCallback(
    (rowKey: string, columnIndex: number, value: string): void => {
      if (!data || !mutations) return;
      const write = cellWrite(data, rowKey, columnIndex, value);
      if (!write) return; // read-only column / unknown row — refused, never a write
      const r = mutations.mutateModule(write.targetId, write.op);
      if (r.status === "applied") setEditNote(null);
      else if (r.status === "stale") setEditNote(ts("module.noteStale"));
      else if (r.status === "refused") setEditNote(r.message);
      else setEditNote(ts("module.noteNotEditable"));
    },
    [data, mutations, ts],
  );

  const exportActions = useMemo<ViewExportAction[]>(
    () =>
      data
        ? [{ label: t("indexExportCsv"), run: () => downloadText(tableToCsv(data), "table.csv", "text/csv") }]
        : [],
    [data, t],
  );
  useRegisterViewExport(exportActions);

  if (!data) return null;
  return (
    <>
      {editNote && (
        <div className="text-xs text-amber-400/90" role="status">
          {editNote}
        </div>
      )}
      <ViewTableView
        data={data}
        onEditCell={editable && mutations ? onEditCell : undefined}
        columns={editable && mutations ? columnOps : null}
        focus={enginePlan?.focus}
      />
    </>
  );
}
